import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import Config from '@/Config';
import Resources from '@/Resources';
import analytics from '@/Analytics';
import { useCurrencyStore } from '@/Stores/Web/currencyStore';
import { useProjectContextStore } from '@/Stores/Web/projectContextStore';
import { usePaymentContextStore } from '@/Stores/Web/paymentContextStore';
import { runAsResult } from '@/Utils/AsyncResultUtility';

export function useDisplayCurrencySelector() {
    const currencyStore = useCurrencyStore();
    const paymentContextStore = usePaymentContextStore();
    const { isV1, projectContext } = storeToRefs(useProjectContextStore());
    const {
        selectedDisplayCurrency, currentDisplayCurrencyId, currentCheckoutCurrency,
        checkoutCurrencies, availableCurrenciesList, globalDisplayCurrencies, isCurrencySelectorVisible,
    } = storeToRefs(currencyStore);
    const form = ref(null);
    const isCurrencyModalVisible = ref(false);
    const selectionKey = ref(0);
    const pendingCurrencyId = ref(null);
    const pendingProjectCurrencyId = ref(null);

    const currentActionUrl = Config.navigation.currentActionUrl;
    const changeCurrencyUrl = Config.projectNavigation.changeCurrencyUrl;
    const useProjectCurrenciesLabel = Resources.ProjectHome.UseProjectCurrenciesLabel;

    const pendingCurrency = computed(() => {
        return selectorCurrencies.value.find(currency => currency.currencyID === pendingCurrencyId.value) || {};
    });
    const selectedCurrencyLabel = computed(() => {
        if (isV1.value) {
            return currentCheckoutCurrency.value?.shortName || useProjectCurrenciesLabel.value;
        }
        return selectedDisplayCurrency.value?.shortName || useProjectCurrenciesLabel.value;
    });
    const isUsingProjectCurrencies = computed(() => {
        return isV1.value ? !currentCheckoutCurrency.value : !selectedDisplayCurrency.value;
    });
    const selectorCurrencies = computed(() => {
        return isV1.value
            ? checkoutCurrencies.value
            : globalDisplayCurrencies.value.filter(currency => currency.commissionRate !== null);
    });
    const currencyOptions = computed(() => {
        return selectorCurrencies.value.map(currency => ({
            ...currency,
            isSelected: currentDisplayCurrencyId.value === currency.currencyID,
        }));
    });
    const isProjectCurrenciesOptionSelected = computed(() => {
        return !currentDisplayCurrencyId.value;
    });
    const selectedCurrencyId = computed(() =>
        (isV1.value ? currentCheckoutCurrency.value?.currencyID : currentDisplayCurrencyId.value) || null);

    const selectCurrency = currencyId => runAsResult(async () => {
        if (isV1.value) {
            const currency = checkoutCurrencies.value.find(item => item.currencyID === currencyId);
            if (currency && currency.currencyID !== currentCheckoutCurrency.value?.currencyID) {
                pendingProjectCurrencyId.value = currency.projectCurrencyID;
                await currencyStore.setCurrentDisplayCurrencyID(null);
                trackCurrencyChange(currentCheckoutCurrency.value, currency);
                form.value.submit();
            }
            return;
        }
        if (currencyId === currentDisplayCurrencyId.value) {
            return;
        }
        pendingCurrencyId.value = currencyId;
        isCurrencyModalVisible.value = true;
    });

    function selectProjectCurrency() {
        const previousCurrency = selectedDisplayCurrency.value;
        pendingCurrencyId.value = null;
        currencyStore.setCurrentDisplayCurrencyID(null);
        paymentContextStore.setCurrency(null);
        trackCurrencyChange(previousCurrency, null);
    }

    function applyCurrency(currencyId) {
        const previousCurrency = selectedDisplayCurrency.value;
        currencyStore.setCurrentDisplayCurrencyID(currencyId);
        const currency = availableCurrenciesList.value.find(item => item.currencyID === currencyId);
        if (currency) {
            paymentContextStore.setCurrency(currency);
        }
        isCurrencyModalVisible.value = false;
        pendingCurrencyId.value = null;
        trackCurrencyChange(previousCurrency, selectedDisplayCurrency.value);
    }

    function trackCurrencyChange(previousCurrency, nextCurrency) {
        const fromCurrencyIsoCode = previousCurrency?.shortName || 'use project currencies';
        const toCurrencyIsoCode = nextCurrency?.shortName || 'use project currencies';
        if (fromCurrencyIsoCode === toCurrencyIsoCode) {
            return;
        }

        analytics.track('change_displayed_currency', {
            from_currency_iso_code: fromCurrencyIsoCode,
            to_currency_iso_code: toCurrencyIsoCode,
        }, projectContext.value);
    }

    function cancelCurrency() {
        isCurrencyModalVisible.value = false;
        pendingCurrencyId.value = null;
        selectionKey.value++;
    }

    return {
        form,
        isV1,
        isCurrencySelectorVisible,
        isCurrencyModalVisible,
        selectionKey,
        pendingProjectCurrencyId,
        selectedCurrencyId,
        pendingCurrency,
        selectedCurrencyLabel,
        isUsingProjectCurrencies,
        currencyOptions,
        isProjectCurrenciesOptionSelected,
        currentActionUrl,
        changeCurrencyUrl,
        useProjectCurrenciesLabel,
        selectCurrency,
        selectProjectCurrency,
        applyCurrency,
        cancelCurrency,
    };
}
